import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Snackbar,
  Typography,
} from "@mui/material";
import { UrlApi } from "../../services/apirest";
import { logEventos } from "../../services/services";

const DIAS = [
  { clave: "lunes", nombre: "Lunes" },
  { clave: "martes", nombre: "Martes" },
  { clave: "miercoles", nombre: "Miércoles" },
  { clave: "jueves", nombre: "Jueves" },
  { clave: "viernes", nombre: "Viernes" },
];

const CONFIG_VACIA = {
  lunes: false,
  martes: false,
  miercoles: false,
  jueves: false,
  viernes: false,
};

function ConfigDias(props) {
  const { onClose } = props;

  const [valores, setValores] = useState(CONFIG_VACIA);
  const [originales, setOriginales] = useState(CONFIG_VACIA);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");
  const [cargaFallida, setCargaFallida] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const guardandoRef = useRef(false);

  useEffect(() => {
    let vigente = true;

    fetch(UrlApi + "/config_diashabilitados/ultimaRow")
      .then(async (res) => {
        const data = await res.json().catch(() => null);

        if (!res.ok || !Array.isArray(data)) {
          throw new Error("Respuesta inválida al cargar la configuración");
        }

        if (!vigente) return;

        const fila = data[0] || {};
        const configuracion = DIAS.reduce((resultado, dia) => {
          resultado[dia.clave] = fila[dia.clave] === 1;
          return resultado;
        }, {});

        setValores(configuracion);
        setOriginales(configuracion);
      })
      .catch((err) => {
        console.error(err);
        if (vigente) {
          setCargaFallida(true);
          setError(
            "No se pudo cargar la configuración actual. Para evitar pisarla, no se puede guardar."
          );
        }
      })
      .finally(() => {
        if (vigente) setCargando(false);
      });

    return () => {
      vigente = false;
    };
  }, []);

  const hayCambios = DIAS.some(
    (dia) => valores[dia.clave] !== originales[dia.clave]
  );
  const hayDiasHabilitados = DIAS.some((dia) => valores[dia.clave]);

  const guardarConfiguracion = async () => {
    if (guardandoRef.current || cargaFallida) return;

    guardandoRef.current = true;
    setGuardando(true);
    setError("");

    try {
      const res = await fetch(UrlApi + "/config_diashabilitados", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lunes: valores.lunes ? 1 : 0,
          martes: valores.martes ? 1 : 0,
          miercoles: valores.miercoles ? 1 : 0,
          jueves: valores.jueves ? 1 : 0,
          viernes: valores.viernes ? 1 : 0,
          usuario: localStorage.getItem("user"),
        }),
      });

      const data = await res.json().catch(() => null);

      // Estas rutas pueden responder 200 con el error de base de datos.
      if (!res.ok || data?.sqlMessage || data?.code) {
        setError("No se pudo guardar la configuración. Intentá nuevamente.");
        return;
      }

      logEventos(
        "Guardar configuración de dias",
        "Pedidos Semanales",
        "Se guardó la configuración de días habilitados",
        localStorage.getItem("user")
      );

      setOriginales(valores);
      setMensaje("La configuración se guardó correctamente.");
    } catch (err) {
      console.error(err);
      setError("No se pudo conectar con el servidor. Intentá nuevamente.");
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          mt: 1,
          border: "1px solid",
          borderColor: "divider",
          borderTop: "3px solid #1976D2",
          borderRadius: 1,
          bgcolor: "#E3F2FD",
          p: 1.5,
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
          Días en que las plantas pueden cargar pedidos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Los administradores pueden cargar cualquier día.
        </Typography>

        {cargando ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              columnGap: 2,
              bgcolor: "white",
              borderRadius: 1,
              px: 1,
            }}
          >
            {DIAS.map((dia) => (
              <FormControlLabel
                key={dia.clave}
                label={dia.nombre}
                disabled={cargaFallida || guardando}
                control={
                  <Checkbox
                    checked={valores[dia.clave]}
                    onChange={(e) => {
                      setError("");
                      setValores((anteriores) => ({
                        ...anteriores,
                        [dia.clave]: e.target.checked,
                      }));
                    }}
                  />
                }
              />
            ))}
          </Box>
        )}

        {!cargando && !cargaFallida && !hayDiasHabilitados ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Si guardás así, ninguna planta va a poder cargar pedidos.
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        ) : null}
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
          mt: 1.5,
        }}
      >
        <Button onClick={onClose} disabled={guardando}>
          Volver
        </Button>
        <Button
          variant="contained"
          color="success"
          disabled={!hayCambios || cargando || cargaFallida || guardando}
          onClick={guardarConfiguracion}
        >
          {guardando ? "Guardando..." : "Guardar configuración"}
        </Button>
      </Box>

      <Snackbar
        open={Boolean(mensaje)}
        autoHideDuration={2500}
        onClose={() => setMensaje("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMensaje("")}>
          {mensaje}
        </Alert>
      </Snackbar>
    </>
  );
}

export default ConfigDias;